<template>
  <div ref="selectRef" class="create-option-select">
    <button class="create-option-trigger" type="button" @click="openPanel">
      <span>{{ selectedOption?.label ?? modelValue }}</span>
      <span
        v-if="selectedOption?.membership"
        class="membership-badge"
        :class="`membership-badge--${selectedOption.membership}`"
        :aria-label="membershipLabel(selectedOption.membership)"
      >
        <img
          class="membership-badge__compact"
          :src="membershipIcons[selectedOption.membership].compact"
          alt=""
          aria-hidden="true"
        />
      </span>
      <SvgIcon class="create-option-chevron" name="create-option-chevron" :size="12" />
    </button>

    <div v-if="panelVisible && !isMobile" class="create-option-dropdown">
      <button
        v-for="option in options"
        :key="option.value"
        class="create-option-item"
        :class="{
          active: option.value === modelValue,
          'create-option-item--basic': option.membership === 'basic',
          'create-option-item--premium': option.membership === 'premium'
        }"
        type="button"
        @click="selectOption(option.value)"
      >
        <span>{{ option.label }}</span>
        <span
          v-if="option.membership"
          class="membership-badge membership-badge--expandable"
          :class="`membership-badge--${option.membership}`"
          :aria-label="membershipLabel(option.membership)"
        >
          <img
            class="membership-badge__compact"
            :src="membershipIcons[option.membership].compact"
            alt=""
            aria-hidden="true"
          />
          <img
            class="membership-badge__full"
            :src="membershipIcons[option.membership].full"
            alt=""
            aria-hidden="true"
          />
        </span>
      </button>
    </div>

    <Teleport to="body">
      <Transition name="option-sheet-fade">
        <div v-if="panelVisible && isMobile" class="option-sheet-root">
          <div class="option-sheet-overlay" @click.self="closePanel"></div>
          <div class="option-sheet" role="dialog" :aria-label="sheetTitle">
            <header class="option-sheet-header">
              <span class="option-sheet-title">{{ sheetTitle }}</span>
              <button
                class="option-sheet-close"
                type="button"
                aria-label="关闭"
                @click="closePanel"
              >
                <span class="option-sheet-close-icon" aria-hidden="true"></span>
              </button>
            </header>
            <div class="option-sheet-list">
              <button
                v-for="option in options"
                :key="option.value"
                class="option-sheet-item"
                :class="{ active: option.value === modelValue }"
                type="button"
                @click="selectOption(option.value)"
              >
                <span>{{ option.label }}</span>
                <span
                  v-if="option.membership"
                  class="membership-badge"
                  :class="`membership-badge--${option.membership}`"
                  :aria-label="membershipLabel(option.membership)"
                >
                  <img
                    class="membership-badge__compact"
                    :src="membershipIcons[option.membership].compact"
                    alt=""
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { hasMembershipAccess } from '@/api/user';
  import { useUpgradeModal } from '@/hooks';
  import { useUserStore } from '@/store';
  import createMembershipBasicIcon from '@/assets/icons/svg/create-membership-basic.svg?url';
  import createMembershipBasicFullIcon from '@/assets/icons/svg/create-membership-basic-full.svg?url';
  import createMembershipPremiumIcon from '@/assets/icons/svg/create-membership-premium.svg?url';
  import createMembershipPremiumFullIcon from '@/assets/icons/svg/create-membership-premium-full.svg?url';

  export type CreateOptionMembership = 'basic' | 'premium';

  interface CreateOptionItem {
    value: string;
    label: string;
    /** 会员门槛：basic 基础会员，premium 高级会员 */
    membership?: CreateOptionMembership;
  }

  const membershipIcons: Record<CreateOptionMembership, { compact: string; full: string }> = {
    basic: {
      compact: createMembershipBasicIcon,
      full: createMembershipBasicFullIcon
    },
    premium: {
      compact: createMembershipPremiumIcon,
      full: createMembershipPremiumFullIcon
    }
  };

  const membershipLabel = (membership: CreateOptionMembership) =>
    membership === 'basic' ? '基础' : '高级';

  const props = withDefaults(
    defineProps<{
      modelValue: string;
      options: CreateOptionItem[];
      sheetTitle?: string;
    }>(),
    {
      sheetTitle: '请选择'
    }
  );

  const emit = defineEmits<{
    'update:modelValue': [value: string];
  }>();

  const userStore = useUserStore();
  const { showUpgrade } = useUpgradeModal();
  const selectRef = ref<HTMLElement | null>(null);
  const panelVisible = ref(false);
  const isMobile = ref(false);

  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  const selectedOption = computed(() =>
    props.options.find((option) => option.value === props.modelValue)
  );

  const closePanel = () => {
    panelVisible.value = false;
    document.removeEventListener('click', handleOutsideClick);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (!selectRef.value?.contains(event.target as Node)) {
      closePanel();
    }
  };

  const openPanel = () => {
    isMobile.value = isMobileViewport();
    panelVisible.value = true;
    if (!isMobile.value) {
      window.setTimeout(() => document.addEventListener('click', handleOutsideClick), 0);
    }
  };

  const selectOption = (value: string) => {
    const option = props.options.find((item) => item.value === value);
    if (!hasMembershipAccess(userStore.userData, option?.membership)) {
      showUpgrade();
      return;
    }

    emit('update:modelValue', value);
    closePanel();
  };

  const handleViewportChange = () => {
    isMobile.value = isMobileViewport();
    if (panelVisible.value && !isMobile.value) {
      window.setTimeout(() => document.addEventListener('click', handleOutsideClick), 0);
    }
  };

  watch([panelVisible, isMobile], ([visible, mobile]) => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = visible && mobile ? 'hidden' : '';
  });

  onMounted(() => {
    isMobile.value = isMobileViewport();
    window.addEventListener('resize', handleViewportChange);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleViewportChange);
    document.removeEventListener('click', handleOutsideClick);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  });
</script>

<style scoped>
  .create-option-select {
    position: relative;
    width: 100%;
  }

  .create-option-trigger {
    width: 100%;
    height: 43px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 16px;
    border: 1px solid rgba(229, 227, 239, 0.92);
    border-radius: 14px;
    outline: 0;
    background: rgba(255, 255, 255, 0.96);
    color: #15121f;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    text-align: left;
    box-shadow: 0 8px 22px rgba(76, 62, 124, 0.04);
    cursor: pointer;
  }

  .create-option-chevron {
    flex-shrink: 0;
    color: #160211;
  }

  .create-option-trigger > span:first-child,
  .create-option-item > span:first-child,
  .option-sheet-item > span:first-child {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .create-option-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 12;
    width: 100%;
    padding: 6px;
    border: 1px solid rgba(229, 227, 239, 0.92);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 14px 34px rgba(76, 62, 124, 0.12);
    box-sizing: border-box;
  }

  .create-option-item {
    width: 100%;
    height: 34px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: #15121f;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    text-align: left;
    cursor: pointer;
  }

  .create-option-item:hover,
  .create-option-item.active {
    background: #f3efff;
    color: #6730e7;
  }

  .create-option-item--premium:hover,
  .create-option-item--premium.active {
    background: #fff6ea;
    color: #d47a19;
  }

  .membership-badge {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 4px;
    transition: width 0.22s ease;
  }

  .membership-badge__compact,
  .membership-badge__full {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    max-width: none;
    display: block;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  .membership-badge__compact {
    width: 16px;
    opacity: 1;
  }

  .membership-badge__full {
    width: 39px;
    opacity: 0;
  }

  .create-option-item:hover .membership-badge--expandable {
    width: 39px;
  }

  .create-option-item:hover .membership-badge--expandable .membership-badge__compact {
    opacity: 0;
  }

  .create-option-item:hover .membership-badge--expandable .membership-badge__full {
    opacity: 1;
  }

  .option-sheet-root {
    position: fixed;
    inset: 0;
    z-index: 45;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .option-sheet-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  .option-sheet {
    position: relative;
    width: 100%;
    max-height: min(420px, 70dvh);
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
    border-radius: 16px 16px 0 0;
    background: #fff;
    box-sizing: border-box;
  }

  .option-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .option-sheet-title {
    color: #160211;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
  }

  .option-sheet-close {
    width: 44px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: #fff;
    cursor: pointer;
  }

  .option-sheet-close-icon {
    width: 8px;
    height: 8px;
    display: block;
    border-right: 1.5px solid #160211;
    border-bottom: 1.5px solid #160211;
    transform: rotate(45deg) translateY(-2px);
  }

  .option-sheet-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: calc(70dvh - 72px);
    overflow-y: auto;
  }

  .option-sheet-item {
    width: 100%;
    min-height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
    text-align: center;
    cursor: pointer;
  }

  .option-sheet-item.active {
    color: #6033e1;
  }

  .option-sheet-fade-enter-active,
  .option-sheet-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .option-sheet-fade-enter-active .option-sheet,
  .option-sheet-fade-leave-active .option-sheet {
    transition: transform 0.2s ease;
  }

  .option-sheet-fade-enter-from,
  .option-sheet-fade-leave-to {
    opacity: 0;
  }

  .option-sheet-fade-enter-from .option-sheet,
  .option-sheet-fade-leave-to .option-sheet {
    transform: translateY(100%);
  }

  @media (max-width: 767px) {
    .create-option-trigger {
      height: 42px;
      padding: 0 16px;
      border: 0;
      border-radius: 12px;
      background: #fff;
      color: #160211;
      font-size: 13px;
      font-weight: 400;
      box-shadow: none;
    }

    .create-option-chevron {
      margin-left: auto;
      color: #160211;
    }
  }
</style>
